import { Button, Card, Icon, Meter, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { PageHeader } from '@/app/layouts/PageHeader'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import { BRAND } from '@/brand/brand'

import { ESIM_PLANS as PLANS } from './esim-data'

const COVERAGE = [
  ['Mexico', 'Telcel · 5G', 'from $4.50'],
  ['Colombia', 'Claro · 5G', 'from $4.50'],
  ['Brazil', 'Vivo · 5G', 'from $5.20'],
  ['Bolivia', 'Entel · 4G', 'from $6.00'],
  ['United States', 'AT&T · 5G', 'from $5.50'],
] as const

const STEPS = [
  { n: '1', title: "Pick where you're going", body: 'Every corridor we send money to is covered, plus 150 other countries.' },
  { n: '2', title: `Pay from your ${BRAND.name} balance`, body: 'It comes off your USD account like any other purchase.' },
  { n: '3', title: 'Scan the QR before you fly', body: 'It switches on by itself when you land.' },
]

export function ESimScreen() {
  return (
    <>
      <PageHeader
        title="Travel eSIM"
        subtitle={`Data in 150+ countries, paid from your ${BRAND.name} balance. No roaming bill waiting for you.`}
        actions={<Button variant="ghost" size="md" iconStart="sim_card" to="/transactions">My eSIMs</Button>}
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Stack direction="row" align="center" gap={2}>
                <Icon name="sim_card" size={18} />
                <Text variant="caption" weight="semibold" tone="onDark">Mexico · Telcel</Text>
              </Stack>
              <Pill tone="accent">Active</Pill>
            </Card.Header>
            <Stack direction="row" justify="between" align="end" gap={4} className="pb-mt-4">
              <Stack direction="row" align="baseline" gap={2}>
                <Text as="p" variant="display" weight="bold" tone="accent" numeric>3.2 GB</Text>
                <Text variant="caption" tone="onDarkMuted">left of 5 GB</Text>
              </Stack>
              <Text variant="caption" tone="onDarkMuted">Ends 3 Oct · 11 days left</Text>
            </Stack>
            <div className="pb-mt-4">
              <Meter value={3.2 / 5} height={10} onDark label="Data remaining" />
            </div>
            <Card.Footer>
              <Stack direction="row" gap={2} wrap align="center">
                <Button size="md" iconStart="add" to="/esim/buy">Add more data</Button>
                <Button variant="accentDim" size="md" to="/esim/qr">See QR code</Button>
                <Text variant="eyebrow" tone="onDarkMuted" className="pb-hide-sm">
                  Your Mexican number keeps working for calls and messages
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Card.Header>
              <Text variant="caption" weight="semibold">Going somewhere else?</Text>
              <Text variant="eyebrow" tone="secondary">150+ countries</Text>
            </Card.Header>
            <Stack gap={3} className="pb-mt-4">
              {PLANS.map((plan) => (
                <Card key={plan.size} tone={plan.featured ? 'dark' : 'plain'} padded={false}>
                  <Stack direction="row" align="center" gap={3} style={{ padding: 'var(--pb-space-4)' }}>
                    <Stack grow gap={1}>
                      <Stack direction="row" align="center" gap={2} wrap>
                        <Text variant="caption" weight="bold" tone={plan.featured ? 'onDark' : 'primary'}>
                          {plan.size} · {plan.days}
                        </Text>
                        {plan.featured && <Pill tone="accent">Free with Plus</Pill>}
                      </Stack>
                      <Text variant="eyebrow" tone={plan.featured ? 'onDarkMuted' : 'secondary'}>
                        {plan.body}
                      </Text>
                    </Stack>
                    <Text variant="subtitle" weight="bold" tone={plan.featured ? 'onDark' : 'primary'} numeric>
                      {plan.price}
                    </Text>
                    <Button variant={plan.featured ? 'primary' : 'outline'} size="sm" to={`/esim/buy?plan=${plan.id}`}>Buy</Button>
                  </Stack>
                </Card>
              ))}
            </Stack>
            <Text variant="caption" tone="secondary" className="pb-mt-4">
              Data only. Paid from your {BRAND.name} balance, so there is no roaming bill and nothing to
              cancel — the plan just ends on its date.
            </Text>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">How it works</Text>
            <div className={`${checkout.reasons} pb-mt-2`}>
              {STEPS.map((step) => (
                <div key={step.n} className={checkout.reason}>
                  <Card tone="dark" padded={false} className="pb-step-dot">
                    <Text variant="eyebrow" weight="bold" tone="accent">{step.n}</Text>
                  </Card>
                  <Stack gap={1}>
                    <Text variant="caption" weight="semibold">{step.title}</Text>
                    <Text variant="caption" tone="secondary">{step.body}</Text>
                  </Stack>
                </div>
              ))}
            </div>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">Your corridors</Text>
            <Text variant="eyebrow" tone="secondary" className="pb-mt-2">
              The five places you already send money to, and what data costs there
            </Text>
            <Stack gap={0} className="pb-mt-4">
              {COVERAGE.map(([country, network, price]) => (
                <div key={country} className={checkout.line}>
                  <Text variant="caption" weight="semibold" className="pb-grow">{country}</Text>
                  <Text variant="caption" tone="secondary" style={{ width: '6rem', textAlign: 'right' }}>{network}</Text>
                  <Text variant="caption" weight="semibold" numeric style={{ width: '5.5rem', textAlign: 'right' }}>{price}</Text>
                </div>
              ))}
            </Stack>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
